import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { HealthCheck } from '@/api/types'
import { systemHealthQuery, systemInfoQuery } from '@/features/platform/platform.queries'
import { formatDuration, formatNumber, formatRelative } from '@/lib/format'
import { Badge, Card, CardHeader, ErrorState, LoadingState, PageHeader, StatCard } from '@/ui'

export const Route = createFileRoute('/_authed/system/')({
  component: SystemPage,
})

function SystemPage() {
  const { t, i18n } = useTranslation()

  const health = useQuery(systemHealthQuery)
  const info = useQuery(systemInfoQuery)

  if (health.isPending) return <LoadingState />
  if (health.isError) {
    return (
      <ErrorState
        error={health.error}
        onRetry={() => {
          void health.refetch()
        }}
      />
    )
  }

  const { database, redis, worker, queue } = health.data

  return (
    <>
      <PageHeader title={t('system.title')} description={t('system.subtitle')} />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <ServiceCard label={t('system.database')} check={database} />
        <ServiceCard label={t('system.redis')} check={redis} />

        {/*
          Three states, not two.

          The worker has no port: its liveness comes from a heartbeat file whose path is
          `WORKER_HEARTBEAT_PATH`, and that is empty by default. So `lastSeenAt: null` means
          "nothing is configured to report", which is the normal development case — NOT that
          the worker is down. Painting that red says the platform is broken when it is running
          perfectly, which is the fastest way to teach somebody to ignore this page.
        */}
        <Card className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium tracking-wide text-text-secondary uppercase">
              {t('system.worker')}
            </p>
            {worker.lastSeenAt === null ? (
              <Badge tone="neutral" hasDot>
                {t('system.notReporting')}
              </Badge>
            ) : (
              <Badge tone={worker.ok ? 'success' : 'danger'} hasDot>
                {worker.ok ? t('system.healthy') : t('system.down')}
              </Badge>
            )}
          </div>
          <p className="text-[0.8125rem] text-text-secondary">
            {worker.lastSeenAt
              ? t('system.lastSeen', { when: formatRelative(worker.lastSeenAt, i18n.language) })
              : t('system.noHeartbeatConfigured')}
          </p>
          {worker.queues.length > 0 ? (
            <p className="text-xs text-text-muted">
              {t('system.serving', { queues: worker.queues.join(', ') })}
            </p>
          ) : null}
        </Card>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <StatCard
          label={t('system.queuePending')}
          value={formatNumber(queue.pending, i18n.language)}
        />
        <StatCard
          label={t('system.queueProcessing')}
          value={formatNumber(queue.processing, i18n.language)}
        />
        <StatCard
          label={t('system.queueFailed')}
          value={formatNumber(queue.failed, i18n.language)}
          isAlarming={queue.failed > 0}
        />
      </div>

      {info.data ? (
        <Card className="mt-4">
          <CardHeader title={t('system.title')} as="h2" />
          <dl className="mt-3 grid gap-2 text-[0.8125rem] sm:grid-cols-2">
            <Row label={t('system.version')} value={info.data.version} />
            <Row label={t('system.nodeVersion')} value={info.data.nodeVersion} />
            <Row
              label={t('system.uptime')}
              value={formatDuration(info.data.uptimeSeconds / 60, i18n.language)}
            />
            <Row label={t('system.environment')} value={info.data.environment} />
          </dl>
        </Card>
      ) : null}
    </>
  )
}

function ServiceCard({ label, check }: { label: string; check: HealthCheck }) {
  const { t } = useTranslation()

  return (
    <Card className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium tracking-wide text-text-secondary uppercase">{label}</p>
        <Badge tone={check.ok ? 'success' : 'danger'} hasDot>
          {check.ok ? t('system.healthy') : t('system.down')}
        </Badge>
      </div>
      <p data-numeric className="text-[0.8125rem] text-text-secondary">
        {check.latencyMs === null
          ? t('common.notSet')
          : t('system.latency', { ms: check.latencyMs })}
      </p>
    </Card>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0 text-text-secondary">{label}</dt>
      <dd className="min-w-0 truncate text-right font-mono text-xs text-text">{value}</dd>
    </div>
  )
}
